import { Order } from '../../order/models/order';
import { OrderLineType } from '../../order/models/order-line-type';
import { OrderTypeEnum } from '../../order/models/order-type.enum';
import { ProductTimeSpan } from './product-time-span';
import { BookingOrderService } from '../services/booking-order.service';
import { Product } from '../../order/models/product';
import { OrderLine } from '../../order/models/order-line';
import { ValueAccessorBase } from '../../general/components/base/value-accessor';
import { ShopSettingsService } from '../../general/services/shop-settings.service';
import { PickupPoint } from './pickup-point';
import { PickupPointGroup } from './pickup-point-group';

export class SingleOrdersCache {
  private _cachedOrders = {};

  constructor(
    private bookingOrderService: BookingOrderService,
  ) {
  }

  get selectedProductTimeSpan(): ProductTimeSpan {
    return this.bookingOrderService.singleCache.timeStampsCache.selectedProductTimeSpan;
  }

  getOrCreateCachedOrder(timeSpan: number): Order {
    const result = this._cachedOrders[timeSpan];
    if (result) {
      return result;
    }

    return this.createCachedOrder(timeSpan);
  }

  getCachedOrder(timeSpan: number): Order {
    return this._cachedOrders[timeSpan];
  }

  get cachedKeys(): number[] {
    return Object.keys(this._cachedOrders).map(x => parseInt(x, 10));
  }

  createCachedOrder(timeSpan: number): Order {
    const hostOrder = this.bookingOrderService.order;
    hostOrder.bookingDate = this.bookingOrderService.selectedBookingDate;
    const orderLines = hostOrder.orderLines.filter(x => x.orderLineType !== OrderLineType.SingleProduct);
    const order = new Order(hostOrder.orderType, hostOrder.productPricelist);
    // orderLines.forEach(x => order.addTypedOrderLine(x));
    this._cachedOrders[timeSpan] = order;
    return order;
  }

  updateCacheOrdersFromServiceOrder() {
    const hostOrder = this.bookingOrderService.order;
    for (const key of Object.keys(this._cachedOrders)) {
      const cachedOrder = this._cachedOrders[key];
      const orderLines = cachedOrder.orderLines.filter(x => x.orderLineType === OrderLineType.SingleProduct);
      for (const orderLine of hostOrder.orderLines) {
        if (orderLine.isOfType(OrderLineType.Fee, OrderLineType.AdditionalProduct)) {
          orderLines.push(orderLine);
        }
      }

      cachedOrder.clearOrderLines();
      for (const orderLine of orderLines) {
        if (!(orderLine.orderLineType === OrderLineType.SingleProduct && orderLine.quantity === 0)) {
          const areaItem = this.bookingOrderService.areaItems.find(i => i.productId === orderLine.productId);
          if (areaItem) {
            orderLine.price = this.bookingOrderService.computePriceByAreaItem(areaItem, orderLine.quantity);
          }
        }
        cachedOrder.addTypedOrderLine(orderLine);
      }
    }
  }

  updateServiceOrderLinesFromCache(timeSpan: number, addAutomatic?: boolean) {
    const order = this.getCachedOrder(timeSpan);
    if (order == null) {
      return;
    }

    const hostOrder = this.bookingOrderService.order;
    const partnerOrderLines = this._getOrderLinesWithPartnersByOrder(hostOrder);
    const previousHostOrderLines = this.bookingOrderService.isMultiOrderMode ? hostOrder.orderLines.slice() : [];
    hostOrder.clearOrderLines();
    const bookingSlots = this.bookingOrderService.selectedBookingSlots;
    const bookingDate = this.bookingOrderService.selectedBookingDate;
    hostOrder.bookingDate = bookingDate;
    const eventDateStart = bookingDate;
    const pickupPointId = this.bookingOrderService.selectedPickupPointId;
    const pickupPointName = this._getPickupPointName(pickupPointId);
    const pickupPointGroupName = this._getPickupPointGroupName(pickupPointId);
    const pickupPointGroupId = this.bookingOrderService.selectedPickupPointGroupId;
    const additionalProductIds = new Set<number>();
    const possibleAdditionalProductIds = new Set<number>();
    const previousProductsQty = {};
    const currentDateProductsQty = {};
    const currentDateProductsPickupPoint = {};
    const currentDateProductsPickupPointGroup = {};
    for (const previousHostOrderLine of previousHostOrderLines) {

      const productId = previousHostOrderLine.productId;
      const areaItem = this.bookingOrderService.areaItems.find(i => i.productId === productId);
      let lineEventDateStart = eventDateStart;
      const bookingSlot = this.bookingOrderService.getFromSelectedBookingSlots(areaItem.areaId);
      if (bookingSlot) {
        lineEventDateStart = bookingSlot.getDateTime(bookingDate);
      }

      if (previousHostOrderLine && previousHostOrderLine.eventDateStart && lineEventDateStart) {
        if (previousHostOrderLine.eventDateStart.getTime() !== lineEventDateStart.getTime()) {
          const orderLine = hostOrder.addOrderLine(
            previousHostOrderLine.orderLineType,
            previousHostOrderLine.name,
            previousHostOrderLine.productId,
            previousHostOrderLine.price,
            previousHostOrderLine.taxRate,
            previousHostOrderLine.quantity,
            previousHostOrderLine.readonly,
            previousHostOrderLine.bookingSlotId,
            previousHostOrderLine.groupSize,
            previousHostOrderLine.pickupPointName,
            previousHostOrderLine.pickupPointId,
            previousHostOrderLine.pickupPointGroupName,
            previousHostOrderLine.pickupPointGroupId,
            previousHostOrderLine.eventDateStart,
            previousHostOrderLine.bookingDate,
          );
          if (previousHostOrderLine.productId in previousProductsQty) {
            previousProductsQty[previousHostOrderLine.productId] += previousHostOrderLine.quantity;
          } else {
            previousProductsQty[previousHostOrderLine.productId] = previousHostOrderLine.quantity;
          }
          if (previousHostOrderLine.isStockProduct) {
            orderLine.isStockProduct = previousHostOrderLine.isStockProduct;
          }
        } else {
          currentDateProductsQty[previousHostOrderLine.productId] = previousHostOrderLine.quantity;
          currentDateProductsPickupPoint[previousHostOrderLine.productId] = previousHostOrderLine.pickupPointId;
          currentDateProductsPickupPointGroup[previousHostOrderLine.productId] = previousHostOrderLine.pickupPointGroupId;
        }
      }
    }

    if (!order.isCacheLinesQtyCalculated) {
      let currentPickUpPointId = null;
      let currentPickupPointGroupId = null;
      order.orderLines.forEach(x => {
        const previousProductQty = x.productId in previousProductsQty ? previousProductsQty[x.productId] : 0;
        x.quantity = x.quantity - previousProductQty > 0 ? x.quantity - previousProductQty : 0;
        if (x.productId in currentDateProductsQty) {
          x.quantity = currentDateProductsQty[x.productId];
          if (x.productId in currentDateProductsPickupPoint) {
            currentPickUpPointId = currentDateProductsPickupPoint[x.productId];
          }
          if (x.productId in currentDateProductsPickupPointGroup) {
            currentPickupPointGroupId = currentDateProductsPickupPointGroup[x.productId];
          }
        }
      });
      order.isCacheLinesQtyCalculated = true;
      this.bookingOrderService.selectedPickupPointId = currentPickUpPointId ? currentPickUpPointId : this.bookingOrderService.selectedPickupPointId;
      this.bookingOrderService.selectedPickupPointGroupId = currentPickupPointGroupId ? currentPickupPointGroupId : this.bookingOrderService.selectedPickupPointGroupId;

    }

    order.orderLines.forEach(x => {
      if (!(x.orderLineType === OrderLineType.SingleProduct && x.quantity === 0)) {
        if (x.orderLineType === OrderLineType.AdditionalProduct) {
          additionalProductIds.add(x.productId);
        }
        const areaItem = this.bookingOrderService.areaItems.find(i => i.productId === x.productId);
        if (areaItem) {

          if (areaItem.additionalProductIds && areaItem.additionalProductIds.length > 0) {
            areaItem.additionalProductIds.forEach(additionalProduct =>
              possibleAdditionalProductIds.add(additionalProduct));
          }
          const linePrice = this.bookingOrderService.computePriceByAreaItem(areaItem, x.quantity);

          let lineBookingSlotId = 0;
          let lineEventDateStart = eventDateStart;
          const bookingSlot = this.bookingOrderService.getFromSelectedBookingSlots(areaItem.areaId);
          if (bookingSlot) {
            lineBookingSlotId = bookingSlot.id;
            lineEventDateStart = bookingSlot.getDateTime(bookingDate);
          }

          const orderLine = hostOrder.addOrderLine(
            x.orderLineType,
            areaItem.productName,
            areaItem.productId,
            linePrice,
            areaItem.taxRate,
            x.quantity,
            x.readonly,
            lineBookingSlotId,
            x.groupSize,
            pickupPointName,
            pickupPointId,
            pickupPointGroupName,
            pickupPointGroupId,
            lineEventDateStart,
            lineEventDateStart,
          );

          orderLine.isStockProduct = areaItem.isStockProduct;

          if (addAutomatic && areaItem.automaticAdditionalProductsIds && areaItem.automaticAdditionalProductsIds.length) {
            this.bookingOrderService.getAutomaticAdditionalProductByIds(areaItem.automaticAdditionalProductsIds).subscribe(
              (autoProd: Product[]) => {
                autoProd.forEach(autoAddProd => {
                  const price = this.bookingOrderService.computePriceByProduct(autoAddProd, x.quantity);
                  if (autoAddProd.isAreaItem) {
                    hostOrder.addOrderLine(
                      OrderLineType.AutomaticAdditionalProduct,
                      autoAddProd.name,
                      autoAddProd.id,
                      price,
                      autoAddProd.taxRate,
                      x.quantity,
                      x.readonly,
                      bookingSlots && bookingSlots.length ? bookingSlots[0].id : null,
                      x.groupSize,
                    );
                  } else {
                    hostOrder.addOrderLine(
                      OrderLineType.AutomaticAdditionalProduct,
                      autoAddProd.name,
                      autoAddProd.id,
                      price,
                      autoAddProd.taxRate,
                      x.quantity,
                      x.readonly,
                    );
                  }
                });
              });
          }
        } else {
          hostOrder.addTypedOrderLine(x);
        }
      }
    });
    if (additionalProductIds.size > 0) {
      additionalProductIds.forEach(additionalProductId => {
        if (!possibleAdditionalProductIds.has(additionalProductId)) {
          const orderLine = hostOrder.getOrderLineByProductId(additionalProductId);
          if (orderLine) {
            hostOrder.deleteOrderLine(orderLine);
          }
        }
      });
    }
    if (partnerOrderLines && partnerOrderLines.length > 0) {
      hostOrder.updatePartnersOrderLine(partnerOrderLines);
    }
    order.sortOrderLines();
  }

  private _getPickupPointName(pickupPointId: number): string {
    const pickupPoint = this._getPickupPoint(pickupPointId);
    return pickupPoint ? pickupPoint.name : '';
  }

  private _getPickupPoint(pickupPointId: number): PickupPoint {
    if (!this.bookingOrderService.areaAreaItemPack) {
      return null;
    }
    const pickupPoints = this.bookingOrderService.areaAreaItemPack.pickupPoints;
    for (const pickUpPoint of pickupPoints) {
      if (pickUpPoint.id === pickupPointId) {
        return pickUpPoint;
      }
    }
    return null;
  }

  private _getPickupPointGroup(pickupPointGroupId: number): PickupPointGroup {
    if (!this.bookingOrderService.areaAreaItemPack) {
      return null;
    }
    const pickupPointGroups = this.bookingOrderService.areaAreaItemPack.pickupPointGroups;
    for (const pickUpPointGroup of pickupPointGroups) {
      if (pickUpPointGroup.id === pickupPointGroupId) {
        return pickUpPointGroup;
      }
    }
    return null;
  }

  private _getPickupPointGroupName(pickupPointId: number): string {
    const pickupPoint = this._getPickupPoint(pickupPointId);
    if (!pickupPoint || !pickupPoint.groupId) {
      return '';
    }

    const pickupPointGroup = this._getPickupPointGroup(pickupPoint.groupId);
    return pickupPointGroup ? pickupPointGroup.name : '';
  }

  private _getOrderLinesWithPartnersByOrder(order: Order): OrderLine[] {
    let partnerOrderLines: Array<OrderLine>;

    if (!order || !order.orderLines) {
      return partnerOrderLines;
    }

    if (order.orderLines.some(value => value.partners != null)) {
      partnerOrderLines = order.orderLines.filter((key) => key.partners != null);
    }
    return partnerOrderLines;
  }

  reset() {
    this._cachedOrders = {};
  }

  resetIsCacheLinesQtyCalculatedForAllChachedOders() {
    const cachedKeys = this.cachedKeys;
    if (!cachedKeys || cachedKeys.length < 1) {
      return;
    }

    for (const cachedKey of cachedKeys) {
      if (cachedKey in this._cachedOrders) {
        this._cachedOrders[cachedKey].isCacheLinesQtyCalculated = false;
      }
    }
  }

  resetCacheLinesQuantity() {
    const cachedKeys = this.cachedKeys;
    if (!cachedKeys || cachedKeys.length < 1) {
      return;
    }

    for (const cachedKey of cachedKeys) {
      if (cachedKey in this._cachedOrders) {
        const orderLines = this._cachedOrders[cachedKey].orderLines;
        if (!orderLines) {
          continue;
        }
        for (const line of orderLines) {
          line.quantity = 0;
        }
      }
    }
  }
}
